import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

// Define the product type
export interface QuoteProduct {
  id: number;
  name: string;
  category: string;
  subcategory?: string;
  description?: string;
  specifications?: string[];
  image?: string;
}

// Define the context interface
interface QuoteContextProps {
  quoteItems: QuoteProduct[];
  addToQuote: (product: QuoteProduct) => void;
  removeFromQuote: (productId: number) => void;
  clearQuote: () => void;
  isInQuote: (productId: number) => boolean;
}

// Create the context
const QuoteContext = createContext<QuoteContextProps | undefined>(undefined);

// Create a provider component
export const QuoteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [quoteItems, setQuoteItems] = useState<QuoteProduct[]>([]);

  // Load quote items from localStorage on initial load
  useEffect(() => {
    const savedQuote = localStorage.getItem('quoteItems');
    if (savedQuote) {
      setQuoteItems(JSON.parse(savedQuote));
    }
  }, []);

  // Save quote items to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('quoteItems', JSON.stringify(quoteItems));
  }, [quoteItems]);

  // Add a product to the quote
  const addToQuote = (product: QuoteProduct) => {
    setQuoteItems(prev => {
      // Check if product is already in quote
      if (prev.some(item => item.id === product.id)) {
        return prev;
      }
      return [...prev, product];
    });
  };

  // Remove a product from the quote
  const removeFromQuote = (productId: number) => {
    setQuoteItems(prev => prev.filter(item => item.id !== productId));
  };

  // Clear all items from the quote
  const clearQuote = () => {
    setQuoteItems([]);
  };

  // Check if a product is in the quote
  const isInQuote = (productId: number) => {
    return quoteItems.some(item => item.id === productId);
  };

  return (
    <QuoteContext.Provider value={{ 
      quoteItems, 
      addToQuote, 
      removeFromQuote, 
      clearQuote,
      isInQuote
    }}>
      {children}
    </QuoteContext.Provider>
  );
};

// Create a hook to use the quote context
export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (context === undefined) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
}; 